import "@fontsource/kumbh-sans/400.css";
import "@fontsource/kumbh-sans/700.css";
import "@fontsource/merriweather/700.css";
import Close from "@mui/icons-material/Close";
import NorthEast from "@mui/icons-material/NorthEast";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useProductAnnouncementBanner } from "components/ProductAnnouncement/useProductAnnouncement";
import { ProductAnnouncementKey } from "generated-graphql/graphql";

import linesBannerGreen from "../../../assets/lines-banner-green.webp";
import { encampBrandColors } from "../../../colors";

export const TIER_II_WEBINAR_URL = "https://explore.encamp.com/ry26-encamp-customer-training";

const MIDDLE_MOUSE_BUTTON = 1;
const TITLE_FONT = '"Merriweather", Georgia, serif';
const BODY_FONT = '"Kumbh Sans", "Roboto", sans-serif';

/**
 * Invites users who can open Chemicals to register for the RY2026 Tier II
 * training series. The server decides who sees it and until when. Matches the
 * marketing banner in the Design System Specs Figma (node 1444:840) in both
 * light and dark mode.
 */
export function TierIIWebinarBanner() {
  const { visible, clickCta, dismiss } = useProductAnnouncementBanner(
    ProductAnnouncementKey.TierIiWebinarBanner_2026_10
  );

  if (!visible) {
    return null;
  }

  return (
    <Box
      role="region"
      aria-label="Tier II training"
      sx={{
        position: "relative",
        isolation: "isolate",
        overflow: "hidden",
        display: "flex",
        flexWrap: { xs: "wrap", md: "nowrap" },
        alignItems: "center",
        columnGap: 5,
        rowGap: 1.5,
        pl: { xs: 2, md: 10 },
        pr: { xs: 6, md: 11 },
        py: { xs: 2, md: 3 },
        color: encampBrandColors.white,
        backgroundImage: `linear-gradient(to right, ${encampBrandColors.yellowGreen}, ${encampBrandColors.sapphire})`,
        // The topo line art sits at 40% opacity between the gradient and the content.
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          zIndex: -1,
          opacity: 0.4,
          backgroundImage: `url(${linesBannerGreen})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "125% auto",
          backgroundPosition: "0% 20%",
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "grid", gap: 0.5 }}>
        <Typography
          component="p"
          sx={{
            fontFamily: TITLE_FONT,
            fontWeight: 700,
            fontSize: { xs: 18, md: 24 },
            lineHeight: "normal",
          }}
        >
          Get ready for Tier II Season
        </Typography>
        <Typography
          component="p"
          sx={{ fontFamily: BODY_FONT, fontSize: { xs: 16, md: 22 }, lineHeight: "normal" }}
        >
          Join our free live training during any of our Wednesday sessions, Nov 4 – Jan 20.
        </Typography>
      </Box>
      {/* A plain new-tab link, so it opens even when the click can't be recorded. */}
      <Button
        variant="contained"
        href={TIER_II_WEBINAR_URL}
        target="_blank"
        rel="noopener"
        onClick={clickCta}
        onAuxClick={(event) => {
          if (event.button === MIDDLE_MOUSE_BUTTON) {
            clickCta();
          }
        }}
        endIcon={<NorthEast aria-hidden />}
        sx={{
          flexShrink: 0,
          ml: { md: "auto" },
          px: 2.75,
          py: 1,
          borderRadius: 5,
          fontFamily: BODY_FONT,
          fontWeight: 700,
          fontSize: 16,
          lineHeight: "26px",
          letterSpacing: "0.46px",
          textTransform: "none",
          bgcolor: encampBrandColors.pumpkin,
          color: encampBrandColors.white,
          "&:hover": { bgcolor: alpha(encampBrandColors.pumpkin, 0.9) },
          "& .MuiButton-endIcon > *:nth-of-type(1)": { fontSize: 24 },
        }}
      >
        Register
      </Button>
      <IconButton
        aria-label="Close Tier II training banner"
        size="small"
        color="inherit"
        onClick={dismiss}
        sx={{ position: "absolute", top: { xs: 8, md: 20 }, right: { xs: 8, md: 20 }, p: 0.5 }}
      >
        <Close />
      </IconButton>
    </Box>
  );
}
